import { useMemo } from "react";

import { getModelByPath, getModelRequiredTupleByPath, isReferencedNodeValue } from "@doitintl/cloudflow-commons";
import {
  ComparisonOperator,
  type ConditionExpression,
  ConditionExpressionType,
  type ListApiServiceModelDescriptor,
  type Member,
  ModelType,
  type ReferencedNodeValue,
  type StringApiServiceModelDescriptor,
  type UnwrappedApiServiceModelDescriptor,
} from "@doitintl/cmp-models";
import * as Yup from "yup";

import { type NodeWitOutputModel } from "../../ApiActionParametersForm/parameters/wrappers/ReferencedField/useReferencedFieldContext";
import {
  createReferencedNodeSchema,
  generateApiActionParametersSchema,
} from "../../ApiActionParametersForm/useApiActionParametersSchema";

const defaultModel: StringApiServiceModelDescriptor = { type: ModelType.STRING };

const defaultOperators = [ComparisonOperator.EQUALS, ComparisonOperator.NOT_EQUALS];
const listOfPrimitivesOperators = [
  ComparisonOperator.CONTAINS,
  ComparisonOperator.NOT_CONTAINS,
  ComparisonOperator.IS_NULL,
  ComparisonOperator.IS_NOT_NULL,
];
const numericOperators = [
  ComparisonOperator.EQUALS,
  ComparisonOperator.NOT_EQUALS,
  ComparisonOperator.IS_NULL,
  ComparisonOperator.IS_NOT_NULL,
  ComparisonOperator.GREATER_THAN,
  ComparisonOperator.GREATER_THAN_OR_EQUAL,
  ComparisonOperator.LESS_THAN,
  ComparisonOperator.LESS_THAN_OR_EQUAL,
];
const stringOperators = [
  ComparisonOperator.EQUALS,
  ComparisonOperator.NOT_EQUALS,
  ComparisonOperator.IS_NULL,
  ComparisonOperator.IS_NOT_NULL,
  ComparisonOperator.IN,
  ComparisonOperator.NOT_IN,
  ComparisonOperator.LENGTH_EQUALS,
  ComparisonOperator.LENGTH_GREATER,
  ComparisonOperator.LENGTH_SMALLER,
  ComparisonOperator.GREATER_THAN,
  ComparisonOperator.LESS_THAN,
  ComparisonOperator.LESS_THAN_OR_EQUAL,
  ComparisonOperator.GREATER_THAN_OR_EQUAL,
];
const booleanOperators = [
  ComparisonOperator.EQUALS,
  ComparisonOperator.NOT_EQUALS,
  ComparisonOperator.IS_NULL,
  ComparisonOperator.IS_NOT_NULL,
];

const complexModelOperators = [
  ComparisonOperator.LENGTH_EQUALS,
  ComparisonOperator.LENGTH_GREATER,
  ComparisonOperator.LENGTH_SMALLER,
  ComparisonOperator.HAS_KEY,
  ComparisonOperator.HAS_VALUE,
];

export type FilterDialogFormValues = ConditionExpression & {
  fieldReference: ReferencedNodeValue;
};

export const isComplexModelType = (type: ModelType) =>
  [ModelType.LIST, ModelType.STRUCTURE, ModelType.MAP, ModelType.UNION].includes(type);

function isListOfPrimitives(model: UnwrappedApiServiceModelDescriptor): model is ListApiServiceModelDescriptor<Member> {
  return model.type === ModelType.LIST && !isComplexModelType(model.member.model.type);
}

function isStringModel(model: UnwrappedApiServiceModelDescriptor): model is StringApiServiceModelDescriptor {
  return model.type === ModelType.STRING;
}

export const getOperatorsByModel = (model: UnwrappedApiServiceModelDescriptor) => {
  switch (true) {
    case isListOfPrimitives(model):
      return listOfPrimitivesOperators;
    case [ModelType.INTEGER, ModelType.FLOAT, ModelType.TIMESTAMP].includes(model.type):
      return numericOperators;
    case model.type === ModelType.STRING:
      return stringOperators;
    case model.type === ModelType.BOOLEAN:
      return booleanOperators;
    case isComplexModelType(model.type):
      return complexModelOperators;
    default:
      return defaultOperators;
  }
};

export function getCriteriaValueModel(
  referencedModel: UnwrappedApiServiceModelDescriptor,
  operator: ComparisonOperator
): UnwrappedApiServiceModelDescriptor {
  if (
    [ComparisonOperator.LENGTH_EQUALS, ComparisonOperator.LENGTH_GREATER, ComparisonOperator.LENGTH_SMALLER].includes(
      operator
    )
  ) {
    return {
      type: ModelType.INTEGER,
      min: 0,
    };
  }

  if (isListOfPrimitives(referencedModel)) {
    return referencedModel.member.model;
  }
  if (isComplexModelType(referencedModel.type)) {
    return defaultModel;
  }
  if ([ComparisonOperator.IN, ComparisonOperator.NOT_IN].includes(operator)) {
    return {
      type: ModelType.LIST,
      member: {
        model: referencedModel,
      },
      memberName: "Value",
    };
  }
  return referencedModel;
}

export function useFilterFormSchema(
  selectedNode: NodeWitOutputModel,
  referenceableNodes: NodeWitOutputModel[],
  condition: ConditionExpression | undefined
) {
  const schema = useMemo(
    () =>
      Yup.object({
        fieldReference: createReferencedNodeSchema(referenceableNodes).label("Referenced field").default({
          referencedNodeId: selectedNode.id,
          referencedField: [],
        }),

        field: Yup.ref<string[]>("fieldReference.referencedField"),

        type: Yup.mixed<ConditionExpressionType>()
          .defined()
          .default(ConditionExpressionType.STATIC)
          .when("value", ([value], schema) =>
            schema.transform(() =>
              isReferencedNodeValue(value) ? ConditionExpressionType.REFERENCED : ConditionExpressionType.STATIC
            )
          ),

        comparisonOperator: Yup.mixed<ComparisonOperator>()
          .defined()
          .default(ComparisonOperator.EQUALS)
          .when("fieldReference.referencedField", ([referencedField], schema) => {
            if (!Array.isArray(referencedField)) {
              return schema;
            }
            const referencedModel = getModelByPath(selectedNode.outputModel, referencedField);
            const operators = getOperatorsByModel(referencedModel);
            return schema
              .oneOf(operators)
              .default(operators[0])
              .transform(function t(value) {
                return operators.includes(value) ? value : this.getDefault();
              });
          }),
        value: Yup.mixed<ReferencedNodeValue>()
          .defined()
          .when(["fieldReference.referencedField", "comparisonOperator"], ([referencedField, operator]) => {
            if (operator === ComparisonOperator.IS_NULL || operator === ComparisonOperator.IS_NOT_NULL) {
              return Yup.string().nullable().default(null);
            }

            if (!Array.isArray(referencedField)) {
              return Yup.string().default("");
            }

            const referencedModel = getModelByPath(selectedNode.outputModel, referencedField);
            const model = getCriteriaValueModel(referencedModel, operator);
            const [, isRequired] = getModelRequiredTupleByPath(selectedNode.outputModel, referencedField);

            const wrapperModel: UnwrappedApiServiceModelDescriptor = {
              type: ModelType.STRUCTURE,
              members: { value: { model } },
            };
            // * if the model is a string and is not required, we allow empty strings as filter value
            // * all non-string models are required
            if (isRequired || !isStringModel(model)) {
              wrapperModel.requiredMembers = ["value"];
            }

            return generateApiActionParametersSchema(
              model,
              true,
              {
                referenceableNodes,
                model: wrapperModel,
              },
              "Value",
              [ComparisonOperator.IN, ComparisonOperator.NOT_IN].includes(operator)
            );
          }),
      }),
    [referenceableNodes, selectedNode.id, selectedNode.outputModel]
  );

  const initialValues: FilterDialogFormValues = useMemo(
    () =>
      condition
        ? schema.cast({
            ...condition,
            fieldReference: { referencedField: condition.field, referencedNodeId: selectedNode.id },
          })
        : { ...schema.getDefault(), field: [] },
    [condition, schema, selectedNode.id]
  );

  return [schema, initialValues] as const;
}
